<template>
    <div class="d-flex flex-wrap-none">
        <UserManagementNav active-component="groups" />
        <div class="admin-content">
            <LoadingContainer :promise="loadingPromise">
                <div class="float-right">
                    <button class="btn btn-primary btn-sm" @click.prevent="showRoleCreateModal">New Group</button>
                </div>
                <h1 class="admin-content-heading" id="GroupsHeading">Groups</h1>
                <VueDataTable :headers="activeColumns" :items="roles" sort-by="Name" sort-type="desc" table-class-name="vue-data-table table-sm" theme-color="#3f51b5" must-sort hide-footer :rows-per-page="100000" :prevent-context-menu-row="false">
                    <!-- Headers -->
                    <template #header-icon>
                        <span class="sr-only">Type</span>
                    </template>
                    <template #header-action>
                        <span class="sr-only">Action</span>
                    </template>

                    <!-- Rows -->
                    <!-- eslint-disable-next-line vue/no-unused-vars -->
                    <template #body="{ key, ...items }">
                        <tbody>
                            <tr v-for="role in Object.values(roleDtoCast(items))">
                                <td class="icon" :title="role.Subtype" :class="getGroupIcon(role.Subtype)">
                                    <span class="sr-only">{{ role.Subtype }}</span>
                                </td>
                                <td>{{ role.DisplayLabel == null ? role.Name : role.DisplayLabel }}</td>
                                <td>{{ role.Description }}</td>
                                <td class="text-right nowrap">
                                    <button class="passthrough icon icon-settings" title="Settings" aria-label="Settings" @click.prevent="showEditModal(role, $event)" />
                                    <button class="passthrough icon icon-trash" title="Delete" aria-label="Delete" @click.prevent="deleteRole(role)" />
                                    <AsyncOperationStatus :promise="deletePromises[role.Name]" />
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </VueDataTable>
            </LoadingContainer>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, inject, onActivated, onDeactivated } from "vue";
import { throwIfNonSuccessStatus, fetchXsrf } from "@/lib/fetchHelpers.3";
import AsyncOperationStatus from "@/lib/AsyncOperationStatus.3.vue";
import LoadingContainer from "@/lib/LoadingContainer.3.vue";
import getData from "../../lib/getData";
import GroupEditModal from "./GroupEditModal.3.vue";
import GroupCreateModal from "./GroupCreateModal.3.vue";
import UserManagementNav from "./UserManagementNav.3.vue";
import "vue3-easy-data-table/dist/style.css";
import VueDataTable from "vue3-easy-data-table";
import { useMainStore } from "@/mainStore";
import { showModalInjectKey } from "@/AppShell.3.vue.InjectKeys";
import { RoleDto } from "@cs/RoleDto";
import { ContainerDto } from "@cs/ContainerDto";
import { RoleTypeDto } from "@cs/RoleTypeDto";
import { UserAndFederateRequest } from "@cs/UserAndFederateRequest";

export default defineComponent({
    components: {
        AsyncOperationStatus,
        VueDataTable,
        LoadingContainer,
        UserManagementNav,
    },
    setup() {
        const mainStore = useMainStore();
        const showModal = inject(showModalInjectKey)!;

        const container = ref<ContainerDto>(null!);
        const deletePromises = ref<Record<string, Promise<void>>>({});
        const loadingPromise = ref<Promise<void>>(null!);
        const usersPromise = ref<Promise<UserAndFederateRequest>>(null!);
        const groupTypes = ref(null);
        const showPermissions = ref(false);
        const activeColumns = ref([
            {
                text: "",
                value: "icon",
                sortable: false,
                width: 30,
            },
            {
                text: "Name",
                value: "Name",
                sortable: true,
            },
            {
                text: "Description",
                value: "Description",
                sortable: true,
            },
            {
                text: "",
                value: "action",
                sortable: false,
                width: 1,
            },
        ]);

        const roles = computed(() => {
            return container.value == null ? [] : container.value.Roles.filter(i => i.Type != RoleTypeDto.Role);
        });

        const init = async (refreshCache: boolean) => {
            const groupData = await getData(mainStore, refreshCache ? null : "AdminUserManagementGroups", () => mainStore.getUrl("adminUserManagementUsersGroupsData"));
            container.value = groupData.Container;
            groupTypes.value = groupData.GroupTypes;
            usersPromise.value = fetchXsrf(mainStore, await mainStore.getUrl("adminUserManagementUsersData")).then(r => {
                throwIfNonSuccessStatus(r);
                return r.json();
            });
            showPermissions.value = await mainStore.isFeatureFlagActiveAsync("GroupPermissions");
        };
        const showEditModal = async (role: RoleDto, event: Event) => {
            showModal(GroupEditModal, {
                roleInput: role,
                users: await usersPromise.value,
                groupTypes: groupTypes.value,
                containerData: container.value,
                savingCallback: (newRole: RoleDto) => {
                    return _saveRole(newRole);
                },
                updateCallback: (newRole: RoleDto) => {
                    return _updateRole(newRole);
                },
                deleteCallback: () => {
                    return _deleteRole(role);
                },
                showPermissions: showPermissions.value,
                triggerElement: event.target,
            });
        };
        const _saveRole = async (role: RoleDto) => {
            const response = await fetchXsrf(mainStore, await mainStore.getUrl("adminUserManagementAlterGroup"), {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(role),
            });
            throwIfNonSuccessStatus(response);
            _updateRole(await response.json());
        };
        const _updateRole = async (role: RoleDto) => {
            const index = container.value.Roles.findIndex(i => i.Name == role.Name);
            container.value.Roles.splice(index, 1, role);
        };
        const deleteRole = async (role: RoleDto) => {
            if (!confirm("Delete this group?")) {
                return;
            }
            deletePromises.value[role.Name] = _deleteRole(role);
        };
        const _deleteRole = async (role: RoleDto) => {
            const response = await fetchXsrf(mainStore, await mainStore.getUrl("adminUserManagementDeleteGroup"), {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(role.Name),
            });
            throwIfNonSuccessStatus(response);
            const index = container.value.Roles.findIndex(i => i.Name == role.Name);
            container.value.Roles.splice(index, 1);
        };
        const showRoleCreateModal = (event: Event) => {
            showModal(GroupCreateModal, {
                containerData: container.value,
                groupTypes: groupTypes.value,
                createCallback: (newRole: RoleDto) => {
                    return _createRole(newRole);
                },
                triggerElement: event.target,
            });
        };
        const getGroupIcon = (type: string) => {
            var icon = "";
            switch (type) {
                case "Montana AD":
                    icon = "icon-ad";
                    break;
                case "OpenIdConnect":
                    icon = "icon-cloud";
                    break;
                default:
                    icon = "icon-users";
            }
            return icon;
        };
        const _createRole = async (role: RoleDto) => {
            const response = await fetchXsrf(mainStore, await mainStore.getUrl("adminUserManagementCreateGroup"), {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(role),
            });
            throwIfNonSuccessStatus(response);
            container.value.Roles.push(await response.json());
        };

        onActivated(() => {
            if (loadingPromise.value == null) {
                loadingPromise.value = init(true);
            }
        });

        onDeactivated(() => {
            loadingPromise.value = null!;
        });

        loadingPromise.value = init(false);

        return {
            loadingPromise,
            deletePromises,
            activeColumns,

            roles,

            showEditModal,
            deleteRole,
            showRoleCreateModal,
            getGroupIcon,

            // Vue-tsc workarounds
            //eslint-disable-next-line @typescript-eslint/no-explicit-any
            roleDtoCast: (i: any[]) => i as RoleDto[],
        };
    },
});
</script>
